import React from 'react';

import { sortBy, keyBy, compact } from 'lodash';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Tabs, Tooltip, Typography, Flex } from 'antd';
import { faComment, faFileImage, faHeart, faComment as faCommentPlain, faFileExcel } from '@fortawesome/pro-solid-svg-icons';
// @ts-ignore
import domtoimage from 'dom-to-image';
// @ts-ignore
import { saveAs } from 'file-saver';

import { getOptionScore, getScorePart } from '../../../utils/ranking';
import { ResultOption } from './ResultOption';

import { useTranslation } from 'react-i18next';
import { RadarChart } from 'components/chart/RadarChart';
import Nav from 'components/controls/Nav';
import Table from 'components/Table';
import { ExportTab } from './Tabs/ExportTab';
import * as ExcelJS from 'exceljs';

import OptionEditionModal from 'components/Modal/OptionEditionModal';
import { useUpdateOptionMutation } from 'graphql/generated';

import './Results.sass';

// Function to generate Excel export
const generateExcel = async (options: any[], stakefactors: any[], indicators: any[], constraints: any[], scoresList: any[], t: any) => {
	const workbook = new ExcelJS.Workbook();
	workbook.created = new Date();

	// Helper function to format header
	const formatHeader = (sheet: any) => {
		// Augmenter la hauteur de la ligne d'en-tête
		sheet.getRow(1).height = 30;

		sheet.getRow(1).eachCell((cell: any) => {
			cell.fill = {
				type: 'pattern',
				pattern: 'solid',
				fgColor: { argb: 'FFA6B9C8' },
			};
			cell.font = {
				bold: true,
				size: 14
			};
			cell.alignment = {
				horizontal: 'center',
				vertical: 'middle',
				wrapText: true
			};
			cell.border = {
				top: { style: 'thin', color: { argb: 'FFE0E0E0' } },
				left: { style: 'thin', color: { argb: 'FFE0E0E0' } },
				bottom: { style: 'thin', color: { argb: 'FFE0E0E0' } },
				right: { style: 'thin', color: { argb: 'FFE0E0E0' } }
			};
		});
	};

	// 1. Onglet Stake (Indicators)
	if (indicators.length > 0) {
		const stakeSheet = workbook.addWorksheet(t('models.indicator.name_plural'));
		const stakeColumns = [
			{ header: t('models.indicator.name'), key: 'name', width: 80 },
			{ header: t('models.indicator.fields.description') || 'Description', key: 'description', width: 150 },
			{ header: 'Weight', key: 'weight', width: 15 }
		];
		stakeSheet.columns = stakeColumns;

		// Trier par nom
		const sortedIndicators = sortBy(indicators, 'name');
		const stakeData = [];

		sortedIndicators.forEach((indicator: any, index: number) => {
			// Ajouter un saut de ligne avant un élément avec poids 0, sauf si c'est le premier
			if (indicator.weight === 0 && index > 0) {
				stakeData.push({ name: '', description: '', weight: '' }); // Ligne vide
			}

			stakeData.push({
				name: indicator.name,
				description: indicator.description || '',
				weight: indicator.weight === 0 ? '' : indicator.weight
			});
		});

		stakeSheet.addRows(stakeData);

		// Mettre en valeur les titres (poids = 0) sans background
		let currentRow = 2; // Commencer après l'en-tête
		sortedIndicators.forEach((indicator: any, index: number) => {
			if (indicator.weight === 0) {
				// Si ce n'est pas le premier élément, on a ajouté une ligne vide avant
				if (index > 0) {
					currentRow++; // Passer la ligne vide
				}

				const excelRow = stakeSheet.getRow(currentRow);
				excelRow.eachCell((cell: any) => {
					cell.font = { bold: true, size: 14, color: { argb: 'FF999999' } };
					// Pas de background pour les titres
					cell.alignment = { wrapText: true, vertical: 'top' };
					cell.border = {
						top: { style: 'thin', color: { argb: 'FFE0E0E0' } },
						left: { style: 'thin', color: { argb: 'FFE0E0E0' } },
						bottom: { style: 'thin', color: { argb: 'FFE0E0E0' } },
						right: { style: 'thin', color: { argb: 'FFE0E0E0' } }
					};
				});
			}
			currentRow++;
		});

		// Activer le retour à la ligne et bordures pour toutes les cellules
		stakeSheet.eachRow((row: any) => {
			row.eachCell((cell: any) => {
				cell.alignment = { wrapText: true, vertical: 'top' };
				cell.border = {
					top: { style: 'thin', color: { argb: 'FFE0E0E0' } },
					left: { style: 'thin', color: { argb: 'FFE0E0E0' } },
					bottom: { style: 'thin', color: { argb: 'FFE0E0E0' } },
					right: { style: 'thin', color: { argb: 'FFE0E0E0' } }
				};
			});
		});

		// Ajuster automatiquement la hauteur des lignes
		stakeSheet.eachRow((row: any) => {
			row.height = undefined; // Permet l'ajustement automatique
		});

		formatHeader(stakeSheet);
	}

	// 2. Onglet Requirements (Constraints)
	if (constraints.length > 0) {
		const requirementsSheet = workbook.addWorksheet(t('models.constraint.name_plural'));
		const requirementsColumns = [
			{ header: t('models.constraint.name'), key: 'name', width: 80 },
			{ header: t('models.constraint.fields.description') || 'Description', key: 'description', width: 150 },
			{ header: 'Weight', key: 'weight', width: 15 }
		];
		requirementsSheet.columns = requirementsColumns;

		// Trier par nom
		const sortedConstraints = sortBy(constraints, 'name');
		const requirementsData = [];

		sortedConstraints.forEach((constraint: any, index: number) => {
			// Ajouter un saut de ligne avant un élément avec poids 0, sauf si c'est le premier
			if (constraint.weight === 0 && index > 0) {
				requirementsData.push({ name: '', description: '', weight: '' }); // Ligne vide
			}

			requirementsData.push({
				name: constraint.name,
				description: constraint.description || '',
				weight: constraint.weight === 0 ? '' : constraint.weight
			});
		});

		requirementsSheet.addRows(requirementsData);

		// Mettre en valeur les titres (poids = 0) sans background
		let currentRow = 2; // Commencer après l'en-tête
		sortedConstraints.forEach((constraint: any, index: number) => {
			if (constraint.weight === 0) {
				// Si ce n'est pas le premier élément, on a ajouté une ligne vide avant
				if (index > 0) {
					currentRow++; // Passer la ligne vide
				}

				const excelRow = requirementsSheet.getRow(currentRow);
				excelRow.eachCell((cell: any) => {
					cell.font = { bold: true, size: 14, color: { argb: 'FF999999' } };
					// Pas de background pour les titres
					cell.alignment = { wrapText: true, vertical: 'top' };
					cell.border = {
						top: { style: 'thin', color: { argb: 'FFE0E0E0' } },
						left: { style: 'thin', color: { argb: 'FFE0E0E0' } },
						bottom: { style: 'thin', color: { argb: 'FFE0E0E0' } },
						right: { style: 'thin', color: { argb: 'FFE0E0E0' } }
					};
				});
			}
			currentRow++;
		});

		// Activer le retour à la ligne et bordures pour toutes les cellules
		requirementsSheet.eachRow((row: any) => {
			row.eachCell((cell: any) => {
				cell.alignment = { wrapText: true, vertical: 'top' };
				cell.border = {
					top: { style: 'thin', color: { argb: 'FFE0E0E0' } },
					left: { style: 'thin', color: { argb: 'FFE0E0E0' } },
					bottom: { style: 'thin', color: { argb: 'FFE0E0E0' } },
					right: { style: 'thin', color: { argb: 'FFE0E0E0' } }
				};
			});
			// Permettre la hauteur automatique pour chaque ligne
			row.height = undefined;
		});

		formatHeader(requirementsSheet);
	}

	// 3. Onglet Leadership Neighbours (Stakefactors)
	if (stakefactors.length > 0) {
		const leadershipSheet = workbook.addWorksheet(t('models.stakefactor.name_plural'));
		const leadershipColumns = [
			{ header: t('models.stakefactor.name'), key: 'name', width: 80 },
			{ header: t('models.stakefactor.fields.description') || 'Description', key: 'description', width: 150 },
			{ header: 'Weight', key: 'weight', width: 15 }
		];
		leadershipSheet.columns = leadershipColumns;

		// Trier par nom
		const sortedStakefactors = sortBy(stakefactors, 'name');
		const leadershipData = [];

		sortedStakefactors.forEach((stakefactor: any, index: number) => {
			// Ajouter un saut de ligne avant un élément avec poids 0, sauf si c'est le premier
			if (stakefactor.weight === 0 && index > 0) {
				leadershipData.push({ name: '', description: '', weight: '' }); // Ligne vide
			}

			leadershipData.push({
				name: stakefactor.name,
				description: stakefactor.description || '',
				weight: stakefactor.weight === 0 ? '' : stakefactor.weight
			});
		});

		leadershipSheet.addRows(leadershipData);

		// Mettre en valeur les titres (poids = 0) sans background
		let currentRow = 2; // Commencer après l'en-tête
		sortedStakefactors.forEach((stakefactor: any, index: number) => {
			if (stakefactor.weight === 0) {
				// Si ce n'est pas le premier élément, on a ajouté une ligne vide avant
				if (index > 0) {
					currentRow++; // Passer la ligne vide
				}

				const excelRow = leadershipSheet.getRow(currentRow);
				excelRow.eachCell((cell: any) => {
					cell.font = { bold: true, size: 14, color: { argb: 'FF999999' } };
					// Pas de background pour les titres
					cell.alignment = { wrapText: true, vertical: 'top' };
					cell.border = {
						top: { style: 'thin', color: { argb: 'FFE0E0E0' } },
						left: { style: 'thin', color: { argb: 'FFE0E0E0' } },
						bottom: { style: 'thin', color: { argb: 'FFE0E0E0' } },
						right: { style: 'thin', color: { argb: 'FFE0E0E0' } }
					};
				});
			}
			currentRow++;
		});

		// Activer le retour à la ligne et bordures pour toutes les cellules
		leadershipSheet.eachRow((row: any) => {
			row.eachCell((cell: any) => {
				cell.alignment = { wrapText: true, vertical: 'top' };
				cell.border = {
					top: { style: 'thin', color: { argb: 'FFE0E0E0' } },
					left: { style: 'thin', color: { argb: 'FFE0E0E0' } },
					bottom: { style: 'thin', color: { argb: 'FFE0E0E0' } },
					right: { style: 'thin', color: { argb: 'FFE0E0E0' } }
				};
			});
			// Permettre la hauteur automatique pour chaque ligne
			row.height = undefined;
		});

		formatHeader(leadershipSheet);
	}

	// 4. Onglet de synthèse
	if (scoresList.length > 0) {
		const summarySheet = workbook.addWorksheet('Summary');
		const summaryColumns = [
			{ header: t('models.option.name'), key: 'name', width: 35 },
			{ header: t('models.option.fields.description') || 'Description', key: 'description', width: 60 },
			{ header: `${t('models.indicator.name_plural')} Score`, key: 'indicators', width: 25, style: { alignment: { horizontal: 'right' as const }, numFmt: '0.00' } },
			{ header: `${t('models.constraint.name_plural')} Score`, key: 'constraints', width: 35, style: { alignment: { horizontal: 'right' as const }, numFmt: '0.00' } },
			{ header: `${t('models.stakefactor.name_plural')} Score`, key: 'stakefactors', width: 35, style: { alignment: { horizontal: 'right' as const }, numFmt: '0.00' } },
			{ header: t('models.option.fields.score'), key: 'score', width: 25, style: { alignment: { horizontal: 'right' as const }, numFmt: '0.00' } },
			{ header: t('models.option.fields.gut_short'), key: 'isGutDecision', width: 15 }
		];
		summarySheet.columns = summaryColumns;

		// Ajouter les données avec alignement forcé
		scoresList.forEach((option: any, index: number) => {
			const rowNumber = index + 2; // +2 car on commence après l'en-tête
			const row = summarySheet.addRow([
				option.name,
				option.description || '',
				Number(option.indicators) || 0,
				Number(option.constraints) || 0,
				Number(option.stakefactors) || 0,
				Number(option.score) || 0,
				option.isGutDecision ? 'Yes' : 'No'
			]);

			// Forcer l'alignement à droite pour chaque cellule numérique
			[3, 4, 5, 6].forEach(colIndex => {
				const cell = row.getCell(colIndex);
				// S'assurer que la valeur est un nombre
				cell.value = Number(cell.value);
				// Appliquer le format numérique
				cell.numFmt = '0.00';
				// Appliquer l'alignement à droite
				cell.alignment = { horizontal: 'right', vertical: 'top' };
			});
		});

		// Activer le tri avec Total Score en ordre décroissant par défaut
		summarySheet.autoFilter = {
			from: 'A1',
			to: 'G1',
		};

		// Définir le tri par défaut sur Total Score (colonne G) en ordre décroissant
		summarySheet.getColumn('G').eachCell((cell: any, rowNumber: number) => {
			if (rowNumber === 1) {
				cell.sort = { order: 'desc' };
			}
		});

		// Calculer les meilleurs et pires scores pour chaque colonne numérique
		const columnStats = {
			indicators: { min: Infinity, max: -Infinity },
			constraints: { min: Infinity, max: -Infinity },
			stakefactors: { min: Infinity, max: -Infinity },
			score: { min: Infinity, max: -Infinity }
		};

		scoresList.forEach((option: any) => {
			const indicators = Number(option.indicators) || 0;
			const constraints = Number(option.constraints) || 0;
			const stakefactors = Number(option.stakefactors) || 0;
			const score = Number(option.score) || 0;

			columnStats.indicators.min = Math.min(columnStats.indicators.min, indicators);
			columnStats.indicators.max = Math.max(columnStats.indicators.max, indicators);
			columnStats.constraints.min = Math.min(columnStats.constraints.min, constraints);
			columnStats.constraints.max = Math.max(columnStats.constraints.max, constraints);
			columnStats.stakefactors.min = Math.min(columnStats.stakefactors.min, stakefactors);
			columnStats.stakefactors.max = Math.max(columnStats.stakefactors.max, stakefactors);
			columnStats.score.min = Math.min(columnStats.score.min, score);
			columnStats.score.max = Math.max(columnStats.score.max, score);
		});

		// Ajouter des bordures et aligner les cellules
		summarySheet.eachRow((row: any, rowNumber: number) => {
			row.eachCell((cell: any, colNumber: number) => {
				// Bordures
				cell.border = {
					top: { style: 'thin', color: { argb: 'FFE0E0E0' } },
					left: { style: 'thin', color: { argb: 'FFE0E0E0' } },
					bottom: { style: 'thin', color: { argb: 'FFE0E0E0' } },
					right: { style: 'thin', color: { argb: 'FFE0E0E0' } }
				};

			});
			// Permettre la hauteur automatique pour chaque ligne
			row.height = undefined;
		});

		// Appliquer les couleurs pour les meilleurs et pires scores
		scoresList.forEach((option: any, index: number) => {
			const rowNumber = index + 2;
			const row = summarySheet.getRow(rowNumber);

			const indicators = Number(option.indicators) || 0;
			const constraints = Number(option.constraints) || 0;
			const stakefactors = Number(option.stakefactors) || 0;
			const score = Number(option.score) || 0;

			// Colonne C - Indicators
			if (columnStats.indicators.max !== columnStats.indicators.min) {
				const cell = row.getCell(3);
				if (indicators === columnStats.indicators.max) {
					cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF90EE90' } }; // Vert clair
				} else if (indicators === columnStats.indicators.min) {
					cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFB6C1' } }; // Rouge clair
				}
			}

			// Colonne D - Constraints
			if (columnStats.constraints.max !== columnStats.constraints.min) {
				const cell = row.getCell(4);
				if (constraints === columnStats.constraints.max) {
					cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF90EE90' } }; // Vert clair
				} else if (constraints === columnStats.constraints.min) {
					cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFB6C1' } }; // Rouge clair
				}
			}

			// Colonne E - Stakefactors
			if (columnStats.stakefactors.max !== columnStats.stakefactors.min) {
				const cell = row.getCell(5);
				if (stakefactors === columnStats.stakefactors.max) {
					cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF90EE90' } }; // Vert clair
				} else if (stakefactors === columnStats.stakefactors.min) {
					cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFB6C1' } }; // Rouge clair
				}
			}

			// Colonne F - Score
			if (columnStats.score.max !== columnStats.score.min) {
				const cell = row.getCell(6);
				if (score === columnStats.score.max) {
					cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF90EE90' } }; // Vert clair
				} else if (score === columnStats.score.min) {
					cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFB6C1' } }; // Rouge clair
				}
			}
		});

		formatHeader(summarySheet);
	}

	// Calculer les statistiques par critère pour le formatage des valeurs
	const criteriaStats: {
		indicators: { [key: string]: { min: number; max: number } };
		constraints: { [key: string]: { min: number; max: number } };
		stakefactors: { [key: string]: { min: number; max: number } };
	} = {
		indicators: {},
		constraints: {},
		stakefactors: {}
	};

	// Calculer les min/max pour chaque critère individuellement
	// Pour chaque critère, collecter toutes ses valeurs à travers toutes les options
	indicators.forEach((indicator: any) => {
		if (indicator.weight > 0) {
			const values: number[] = [];
			options.forEach((option: any) => {
				if (option.indicatorsEvaluations) {
					const evaluation = option.indicatorsEvaluations.find((evaluation: any) => evaluation.itemId === indicator.id);
					if (evaluation) {
						values.push(Math.round(Number(evaluation.value) || 0));
					}
				}
			});
			if (values.length > 0) {
				criteriaStats.indicators[indicator.id] = {
					min: Math.min(...values),
					max: Math.max(...values)
				};
			}
		}
	});

	constraints.forEach((constraint: any) => {
		if (constraint.weight > 0) {
			const values: number[] = [];
			options.forEach((option: any) => {
				if (option.constraintsEvaluations) {
					const evaluation = option.constraintsEvaluations.find((evaluation: any) => evaluation.itemId === constraint.id);
					if (evaluation) {
						values.push(Math.round(Number(evaluation.value) || 0));
					}
				}
			});
			if (values.length > 0) {
				criteriaStats.constraints[constraint.id] = {
					min: Math.min(...values),
					max: Math.max(...values)
				};
			}
		}
	});

	stakefactors.forEach((stakefactor: any) => {
		if (stakefactor.weight > 0) {
			const values: number[] = [];
			options.forEach((option: any) => {
				if (option.stakefactorsEvaluations) {
					const evaluation = option.stakefactorsEvaluations.find((evaluation: any) => evaluation.itemId === stakefactor.id);
					if (evaluation) {
						values.push(Math.round(Number(evaluation.value) || 0));
					}
				}
			});
			if (values.length > 0) {
				criteriaStats.stakefactors[stakefactor.id] = {
					min: Math.min(...values),
					max: Math.max(...values)
				};
			}
		}
	});

	// 5. Onglet pour chaque option
	options.forEach((option: any, index: number) => {
		const optionSheet = workbook.addWorksheet(`Option ${index + 1} - ${option.name.substring(0, 20)}`);

		// Définir les largeurs de colonnes
		optionSheet.columns = [
			{ width: 60 }, // A - Name (agrandie)
			{ width: 120 }, // B - Description (réduite)
			{ width: 15 }, // C - Value
			{ width: 60 }  // D - Comment (agrandie)
		];

		// En-tête de l'option avec formatage
		optionSheet.addRow([`${t('models.option.name')}:`, option.name]);
		optionSheet.addRow([`${t('models.option.fields.description') || 'Description'}:`, option.description || '']);
		optionSheet.addRow([`${t('models.option.fields.comments') || 'Comments'}:`, option.comments || '']);
		optionSheet.addRow([`${t('models.option.fields.evaluation')}:`, option.evaluation || '']);
		optionSheet.addRow([`${t('models.option.fields.gut_short')}:`, option.isGutDecision ? 'Yes' : 'No']);
		optionSheet.addRow([]); // Ligne vide

		// Formatage de l'en-tête de l'option
		for (let i = 1; i <= 5; i++) {
			const row = optionSheet.getRow(i);
			row.getCell(1).font = { bold: true, size: 12 };
			row.getCell(1).fill = {
				type: 'pattern',
				pattern: 'solid',
				fgColor: { argb: 'FFA6B9C8' },
			};
		}

		// Évaluations des Stakes (Indicators)
		if (indicators.length > 0) {
			optionSheet.addRow([`${t('models.indicator.name_plural').toUpperCase()} EVALUATION`]);
			optionSheet.addRow([t('models.indicator.name'), t('models.indicator.fields.description') || 'Description', 'Value', 'Comment']);

			// Fusionner les cellules pour le titre sur 4 colonnes (A, B, C, D)
			const titleRow = optionSheet.getRow(optionSheet.lastRow.number - 1);
			optionSheet.mergeCells('A' + (optionSheet.lastRow.number - 1) + ':D' + (optionSheet.lastRow.number - 1));

			// Formatage du titre de section
			const stakesTitleRow = optionSheet.getRow(optionSheet.lastRow.number);
			stakesTitleRow.getCell(1).font = { bold: true, size: 14, color: { argb: 'FF2E8B57' } };
			stakesTitleRow.getCell(1).fill = {
				type: 'pattern',
				pattern: 'solid',
				fgColor: { argb: 'FFA6B9C8' },
			};

			// Formatage de l'en-tête du tableau
			const stakesHeaderRow = optionSheet.getRow(optionSheet.lastRow.number);
			stakesHeaderRow.eachCell((cell: any) => {
				cell.font = { bold: true, size: 12 };
				cell.fill = {
					type: 'pattern',
					pattern: 'solid',
					fgColor: { argb: 'FFA6B9C8' },
				};
			});

			// Trier les indicateurs par nom pour l'ordre alphabétique
			const sortedIndicators = sortBy(indicators, 'name');
			sortedIndicators.forEach((indicator: any) => {
				const evaluation = option.indicatorsEvaluations?.find((evaluation: any) => evaluation.itemId === indicator.id);
				const value = Math.round(evaluation?.value || 0);

				// Pour les éléments sans poids, ne pas afficher le score
				const displayValue = indicator.weight === 0 ? '' : value;
				const displayComment = indicator.weight === 0 ? '' : (evaluation?.description || '');

				const row = optionSheet.addRow([
					indicator.name,
					indicator.description || '',
					displayValue,
					displayComment
				]);

				// Mettre en valeur les éléments sans poids
				if (indicator.weight === 0) {
					row.eachCell((cell: any) => {
						cell.font = { bold: true, color: { argb: 'FF999999' } };
						cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF5F5F5' } }; // Gris très clair
					});
				} else {
					// Appliquer le formatage des couleurs pour la colonne Value (C) seulement pour les éléments avec poids
					const indicatorStats = criteriaStats.indicators[indicator.id];
					if (indicatorStats && indicatorStats.max !== indicatorStats.min) {
						const cell = row.getCell(3);
						if (value === indicatorStats.max) {
							cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF90EE90' } }; // Vert clair
						} else if (value === indicatorStats.min) {
							cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFB6C1' } }; // Rouge clair
						}
					}
				}
			});
			optionSheet.addRow([]); // Ligne vide
		}

		// Évaluations des Requirements (Constraints)
		if (constraints.length > 0) {
			optionSheet.addRow([`${t('models.constraint.name_plural').toUpperCase()} EVALUATION`]);
			optionSheet.addRow([t('models.constraint.name'), t('models.constraint.fields.description') || 'Description', 'Value', 'Comment']);

			// Fusionner les cellules pour le titre sur 4 colonnes (A, B, C, D)
			optionSheet.mergeCells('A' + (optionSheet.lastRow.number - 1) + ':D' + (optionSheet.lastRow.number - 1));

			// Formatage du titre de section
			const requirementsTitleRow = optionSheet.getRow(optionSheet.lastRow.number);
			requirementsTitleRow.getCell(1).font = { bold: true, size: 14, color: { argb: 'FF2E8B57' } };
			requirementsTitleRow.getCell(1).fill = {
				type: 'pattern',
				pattern: 'solid',
				fgColor: { argb: 'FFA6B9C8' },
			};

			// Formatage de l'en-tête du tableau
			const requirementsHeaderRow = optionSheet.getRow(optionSheet.lastRow.number);
			requirementsHeaderRow.eachCell((cell: any) => {
				cell.font = { bold: true, size: 12 };
				cell.fill = {
					type: 'pattern',
					pattern: 'solid',
					fgColor: { argb: 'FFA6B9C8' },
				};
			});

			// Trier les contraintes par nom pour l'ordre alphabétique
			const sortedConstraints = sortBy(constraints, 'name');
			sortedConstraints.forEach((constraint: any) => {
				const evaluation = option.constraintsEvaluations?.find((evaluation: any) => evaluation.itemId === constraint.id);
				const value = Math.round(evaluation?.value || 0);

				// Pour les éléments sans poids, ne pas afficher le score
				const displayValue = constraint.weight === 0 ? '' : value;
				const displayComment = constraint.weight === 0 ? '' : (evaluation?.description || '');

				const row = optionSheet.addRow([
					constraint.name,
					constraint.description || '',
					displayValue,
					displayComment
				]);

				// Mettre en valeur les éléments sans poids
				if (constraint.weight === 0) {
					row.eachCell((cell: any) => {
						cell.font = { bold: true, color: { argb: 'FF999999' } };
						cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF5F5F5' } }; // Gris très clair
					});
				} else {
					// Appliquer le formatage des couleurs pour la colonne Value (C) seulement pour les éléments avec poids
					const constraintStats = criteriaStats.constraints[constraint.id];
					if (constraintStats && constraintStats.max !== constraintStats.min) {
						const cell = row.getCell(3);
						if (value === constraintStats.max) {
							cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF90EE90' } }; // Vert clair
						} else if (value === constraintStats.min) {
							cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFB6C1' } }; // Rouge clair
						}
					}
				}
			});
			optionSheet.addRow([]); // Ligne vide
		}

		// Évaluations des Enablers (Stakefactors)
		if (stakefactors.length > 0) {
			optionSheet.addRow([`${t('models.stakefactor.name_plural').toUpperCase()} EVALUATION`]);
			optionSheet.addRow([t('models.stakefactor.name'), t('models.stakefactor.fields.description') || 'Description', 'Value', 'Comment']);

			// Fusionner les cellules pour le titre sur 4 colonnes (A, B, C, D)
			optionSheet.mergeCells('A' + (optionSheet.lastRow.number - 1) + ':D' + (optionSheet.lastRow.number - 1));

			// Formatage du titre de section
			const enablersTitleRow = optionSheet.getRow(optionSheet.lastRow.number);
			enablersTitleRow.getCell(1).font = { bold: true, size: 14, color: { argb: 'FF2E8B57' } };
			enablersTitleRow.getCell(1).fill = {
				type: 'pattern',
				pattern: 'solid',
				fgColor: { argb: 'FFA6B9C8' },
			};

			// Formatage de l'en-tête du tableau
			const enablersHeaderRow = optionSheet.getRow(optionSheet.lastRow.number);
			enablersHeaderRow.eachCell((cell: any) => {
				cell.font = { bold: true, size: 12 };
				cell.fill = {
					type: 'pattern',
					pattern: 'solid',
					fgColor: { argb: 'FFA6B9C8' },
				};
			});

			// Trier les stakefactors par nom pour l'ordre alphabétique
			const sortedStakefactors = sortBy(stakefactors, 'name');
			sortedStakefactors.forEach((stakefactor: any) => {
				const evaluation = option.stakefactorsEvaluations?.find((evaluation: any) => evaluation.itemId === stakefactor.id);
				const value = Math.round(evaluation?.value || 0);

				// Pour les éléments sans poids, ne pas afficher le score
				const displayValue = stakefactor.weight === 0 ? '' : value;
				const displayComment = stakefactor.weight === 0 ? '' : (evaluation?.description || '');

				const row = optionSheet.addRow([
					stakefactor.name,
					stakefactor.description || '',
					displayValue,
					displayComment
				]);

				// Mettre en valeur les éléments sans poids
				if (stakefactor.weight === 0) {
					row.eachCell((cell: any) => {
						cell.font = { bold: true, color: { argb: 'FF999999' } };
						cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF5F5F5' } }; // Gris très clair
					});
				} else {
					// Appliquer le formatage des couleurs pour la colonne Value (C) seulement pour les éléments avec poids
					const stakefactorStats = criteriaStats.stakefactors[stakefactor.id];
					if (stakefactorStats && stakefactorStats.max !== stakefactorStats.min) {
						const cell = row.getCell(3);
						if (value === stakefactorStats.max) {
							cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF90EE90' } }; // Vert clair
						} else if (value === stakefactorStats.min) {
							cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFB6C1' } }; // Rouge clair
						}
					}
				}
			});
		}

		// Ajouter des bordures aux cellules de données et activer le retour à la ligne
		optionSheet.eachRow((row: any, rowNumber: number) => {
			row.eachCell((cell: any, colNumber: number) => {
				// Bordures
				cell.border = {
					top: { style: 'thin', color: { argb: 'FFE0E0E0' } },
					left: { style: 'thin', color: { argb: 'FFE0E0E0' } },
					bottom: { style: 'thin', color: { argb: 'FFE0E0E0' } },
					right: { style: 'thin', color: { argb: 'FFE0E0E0' } }
				};

				// Alignement et format pour la colonne Value (C)
				if (colNumber === 3) {
					cell.alignment = { horizontal: 'right', vertical: 'top', wrapText: true };
					cell.numFmt = '0';
				} else {
					cell.alignment = { horizontal: 'left', vertical: 'top', wrapText: true };
				}
			});
			// Permettre la hauteur automatique pour chaque ligne
			row.height = undefined;
		});
	});

	const buffer = await workbook.xlsx.writeBuffer();
	saveAs(new Blob([buffer], { type: "application/octet-stream" }), 'Gerositus-Decision-Export.xlsx');
};

function Results(props: any) {
	const { t } = useTranslation();

	const { options = [], stakefactors = [], indicators = [], constraints = [], height, risks = [], riskCategories = [] } = props;
	const [editedItem, setEditedItem] = React.useState<any | null>(null);

	const [updateOption] = useUpdateOptionMutation();

	// Function to export ranking table as image
	const exportRankingAsImage = () => {
		const node = document.querySelector('.Results .ant-table-wrapper');

		if (!node) {
			console.error('Table not found for export');
			return;
		}

		// Wait a bit for the table to be fully rendered
		setTimeout(() => {
			// Get the table element specifically
			const tableElement = node.querySelector('.ant-table');
			const tableBody = node.querySelector('.ant-table-tbody');

			if (!tableElement || !tableBody) {
				console.error('Table elements not found');
				return;
			}

			// Calculate the actual dimensions
			const rect = node.getBoundingClientRect();
			const tableRect = tableElement.getBoundingClientRect();

			// Get the full scroll dimensions
			const scrollWidth = node.scrollWidth;
			const scrollHeight = node.scrollHeight;

			// Calculate the actual content width and height with margins
			const margin = 40; // 20px margin on each side
			// Subtract the width of the comments column since it will be hidden
			const commentsColumnWidth = 100; // Approximate width of comments column
			const actualWidth = Math.max(rect.width, scrollWidth, tableRect.width) - commentsColumnWidth + margin;
			const actualHeight = Math.max(rect.height, scrollHeight, tableRect.height) + margin;

			console.log('Table dimensions:', {
				rectWidth: rect.width,
				rectHeight: rect.height,
				scrollWidth: scrollWidth,
				scrollHeight: scrollHeight,
				tableWidth: tableRect.width,
				tableHeight: tableRect.height,
				actualWidth: actualWidth,
				actualHeight: actualHeight
			});

			// Hide the comments column before capture
			// Try different selectors to find the comments column
			const commentsColumnHeader = node.querySelector('th[data-index="comment"]') ||
				node.querySelector('th:last-child') ||
				node.querySelector('thead th:last-child');
			const commentsColumnCells = node.querySelectorAll('td[data-index="comment"]').length > 0 ?
				node.querySelectorAll('td[data-index="comment"]') :
				node.querySelectorAll('tbody td:last-child');

			// Store original display values
			const originalHeaderDisplay = (commentsColumnHeader as HTMLElement)?.style.display;
			const originalCellsDisplay = Array.from(commentsColumnCells).map(cell => (cell as HTMLElement).style.display);

			console.log('Comments column elements found:', {
				header: commentsColumnHeader,
				cellsCount: commentsColumnCells.length
			});

			// Hide comments column
			if (commentsColumnHeader) {
				(commentsColumnHeader as HTMLElement).style.display = 'none';
				console.log('Comments column header hidden');
			}
			commentsColumnCells.forEach(cell => {
				(cell as HTMLElement).style.display = 'none';
			});
			console.log('Comments column cells hidden:', commentsColumnCells.length);

			// Create copyright element
			const copyrightElement = document.createElement('div');
			copyrightElement.style.cssText = `
				position: absolute;
				bottom: 10px;
				left: 10px;
				right: 0;
				color: #999999;
				font-family: Arial, sans-serif;
				font-size: 12px;
				z-index: 1000;
				pointer-events: none;
				text-align: left;
			`;
			copyrightElement.textContent = `© ${new Date().getFullYear()} Gerositus Sàrl - All rights reserved`;

			// Add copyright to the container
			node.appendChild(copyrightElement);

			domtoimage.toBlob(node, {
				bgcolor: "#ffffff",
				quality: 1,
				width: actualWidth * 2, // Double width for high quality
				height: actualHeight * 2, // Double height for high quality
				filter: (node: any) => {
					if (!node.className || !node.className.match)
						return true;
					// Avoid buttons and other interactive elements
					return !node.className.match('ant-btn') && !node.className.match('ActionsWrapper');
				},
				style: {
					transform: "scale(2)",
					transformOrigin: "top left",
					width: actualWidth + "px",
					height: actualHeight + "px",
					overflow: "hidden", // Change to hidden to avoid extra space
					padding: "20px", // Add padding to ensure margins
					backgroundColor: "transparent"
				}
			})
				.then(function (blob: any) {
					// Remove copyright element after capture
					if (copyrightElement && copyrightElement.parentNode) {
						copyrightElement.parentNode.removeChild(copyrightElement);
					}

					// Restore the comments column visibility
					if (commentsColumnHeader) {
						(commentsColumnHeader as HTMLElement).style.display = originalHeaderDisplay || '';
					}
					commentsColumnCells.forEach((cell, index) => {
						(cell as HTMLElement).style.display = originalCellsDisplay[index] || '';
					});

					const currentDate = new Date().toLocaleDateString().replace(/\//g, '-') + '-' +
						new Date().toLocaleTimeString().replace(/:/g, '-');
					saveAs(blob, `Gerositus-Ranking-${currentDate}.png`);
				})
				.catch(function (error: any) {
					// Remove copyright element even if there's an error
					if (copyrightElement && copyrightElement.parentNode) {
						copyrightElement.parentNode.removeChild(copyrightElement);
					}

					// Restore the comments column visibility even if there's an error
					if (commentsColumnHeader) {
						(commentsColumnHeader as HTMLElement).style.display = originalHeaderDisplay || '';
					}
					commentsColumnCells.forEach((cell, index) => {
						(cell as HTMLElement).style.display = originalCellsDisplay[index] || '';
					});

					console.error('Error generating image:', error);
				});
		}, 200); // Increased timeout to ensure full rendering
	};

	// Function to export visualization (radar chart) as image
	const exportVisualizationAsImage = () => {
		// Use the wrapper element instead of SVG directly
		const node = document.querySelector('.SynthesisGraphWrapper');

		if (!node) {
			console.error('Visualization not found for export');
			return;
		}

		// Wait a bit for the chart to be fully rendered
		setTimeout(() => {
			// Get the actual dimensions of the visualization
			const rect = node.getBoundingClientRect();
			const width = rect.width;
			const height = rect.height;

			// Calculate proper dimensions based on the actual chart size
			// The chart is 700px wide and 600px high as defined in the component
			const chartWidth = 700;
			const chartHeight = 500;
			const margin = 150; // Increased margin to ensure labels are not cut off
			const actualWidth = chartWidth + margin;
			const actualHeight = chartHeight + margin; // Double margin for better vertical centering

			console.log('Visualization dimensions:', {
				rectWidth: width,
				rectHeight: height,
				chartWidth: chartWidth,
				chartHeight: chartHeight,
				actualWidth: actualWidth,
				actualHeight: actualHeight
			});

			// Create copyright element
			const copyrightElement = document.createElement('div');
			copyrightElement.style.cssText = `
				position: absolute;
				bottom: 0px;
				width: 300px;
				left: 0px;
				margin-left: calc(50% - 75px);
				color: #999999;
				font-family: Arial, sans-serif;
				font-size: 12px;
				z-index: 1000;
				pointer-events: none;
				text-align: center;
			`;
			copyrightElement.textContent = `© ${new Date().getFullYear()} Gerositus Sàrl - All rights reserved`;

			// Add copyright to the container
			node.appendChild(copyrightElement);

			domtoimage.toBlob(node, {
				bgcolor: "#ffffff",
				quality: 1,
				width: actualWidth * 2, // High resolution
				height: actualHeight * 2, // High resolution
				filter: (node: any) => {
					if (!node.className || !node.className.match)
						return true;
					// Avoid buttons and other interactive elements
					return !node.className.match('ant-btn') && !node.className.match('ActionsWrapper');
				},
				style: {
					transform: "scale(2)",
					transformOrigin: "top center",
					width: actualWidth + "px",
					height: actualHeight + "px",
					overflow: "visible",
					padding: "75px 75px 10px 75px", // Increased padding to ensure no cropping
					backgroundColor: "transparent",
					display: "flex",
					alignItems: "center",
					justifyContent: "center"
				}
			})
				.then(function (blob: any) {
					// Remove copyright element after capture
					if (copyrightElement && copyrightElement.parentNode) {
						copyrightElement.parentNode.removeChild(copyrightElement);
					}

					const currentDate = new Date().toLocaleDateString().replace(/\//g, '-') + '-' +
						new Date().toLocaleTimeString().replace(/:/g, '-');
					saveAs(blob, `Gerositus-Visualization-${currentDate}.png`);
				})
				.catch(function (error: any) {
					// Remove copyright element even if there's an error
					if (copyrightElement && copyrightElement.parentNode) {
						copyrightElement.parentNode.removeChild(copyrightElement);
					}
					console.error('Error generating visualization image:', error);
				});
		}, 500);
	};

	if (!props.options || props.options.length == 0) {
		return (
			<div className="OptionEvaluation">
				<Typography.Title level={2}>{t("models.option.no_x")}</Typography.Title>
			</div>
		);
	}

	// Update
	const handleUpdateOption = async (id: string, values: Object) => {
		await updateOption({
			variables: {
				id: id,
				...values,
			},
		});

		await props.refetch();
	};

	const scoresList = sortBy(props.options.map((o: any, index: Number) => {
		return {
			...o,
			score: getOptionScore(
				o,
				stakefactors,
				indicators,
				constraints,
				o.stakefactorsEvaluations,
				o.indicatorsEvaluations,
				o.constraintsEvaluations
			),
			indicators: getScorePart(o, indicators, o.indicatorsEvaluations),
			constraints: getScorePart(o, constraints, o.constraintsEvaluations),
			stakefactors: getScorePart(o, stakefactors, o.stakefactorsEvaluations),
			selected: index === props.optionIndex
		};
	}), "score").reverse().map((s, index) => ({ ...s, index: index }));

	const scores = keyBy(
		scoresList,
		'id'
	);

	const currentOption = options[props.optionIndex % options.length];

	const score = scores[options[props.optionIndex]?.id]?.score !== null && scores[options[props.optionIndex]?.id]?.score !== undefined ? scores[options[props.optionIndex]?.id]?.score : null;
	const scoreDetails = scores[options[props.optionIndex]?.id]

	const maxIndicators = scoresList.length > 0 ? Math.max(...scoresList.map((i) => i.indicators)) : 0;
	const maxConstraints = scoresList.length > 0 ? Math.max(...scoresList.map((i) => i.constraints)) : 0;
	const maxStakefactors = scoresList.length > 0 ? Math.max(...scoresList.map((i) => i.stakefactors)) : 0;
	const minIndicators = scoresList.length > 0 ? Math.min(...scoresList.map((i) => i.indicators)) : 0;
	const minConstraints = scoresList.length > 0 ? Math.min(...scoresList.map((i) => i.constraints)) : 0;
	const minStakefactors = scoresList.length > 0 ? Math.min(...scoresList.map((i) => i.stakefactors)) : 0;

	const maxScore = scoresList.length > 0 ? Math.max(...scoresList.map((i) => i.score)) : 0;
	const minScore = scoresList.length > 0 ? Math.min(...scoresList.map((i) => i.score)) : 0;

	const columns = [
		{
			title: '',
			dataIndex: 'index',
			key: 'index',
			width: 40,
			render: (text: any, record: any) => {
				return "#" + (record.index + 1)
			}
		},
		{
			title: <div className="noselection cursor-pointer">{t("models.option.fields.gut_short")}</div>,
			dataIndex: 'gut',
			key: 'gut',
			className: 'text-center pr-1',
			width: 50,
			sortable: false,
			ellipsis: false,
			render: (text: any, record: any) => {
				return (
					<div className="name">
						{record.isGutDecision ? (
							<FontAwesomeIcon icon={faHeart} />
						) : null}
					</div>
				);
			}
		},
		{
			title: <div className="noselection cursor-pointer">{t("models.option.name")}</div>,
			dataIndex: 'name',
			key: 'name',
			className: 'w-50',
			sortable: true,
			ellipsis: true,
			render: (text: any, record: any) => {
				return (
					<div className="name">
						{record.name}
						{record.description ? (
							<div className="description">
								{record.description}
							</div>
						) : (
							''
						)}
					</div>
				);
			}
		},
		{
			title: <div className="noselection cursor-pointer">{t("models.indicator.name_plural")}</div>,
			dataIndex: 'indicators',
			key: 'indicators',
			width: 120,
			className: 'text-center pr-2',
			sortable: (a: any, b: any) => {
				const scoreA = parseInt(a.indicators)
				const scoreB = parseInt(b.indicators)

				if (scoreA < scoreB) return -1;
				if (scoreA > scoreB) return 1;
				return 0
			},
			render: (text: any, record: any) => {
				return (
					<div className={["mr-1", maxIndicators === record?.indicators ? "max" : "", minIndicators === record?.indicators ? "min" : ""].join(" ")}>
						{record?.indicators !== null ? record?.indicators.toFixed(0) : null}
					</div>
				);
			}
		},
		{
			title: <div className="noselection cursor-pointer">{t("models.constraint.name_plural")}</div>,
			dataIndex: 'constraints',
			key: 'constraints',
			width: 120,
			className: 'text-center pr-2',
			sortable: (a: any, b: any) => {
				const scoreA = parseInt(a.constraints)
				const scoreB = parseInt(b.constraints)

				if (scoreA < scoreB) return -1;
				if (scoreA > scoreB) return 1;
				return 0
			},
			render: (text: any, record: any) => {
				return (
					<div className={["mr-1", maxConstraints === record?.constraints ? "max" : "", minConstraints === record?.constraints ? "min" : ""].join(" ")}>
						{record?.constraints !== null ? record?.constraints.toFixed(0) : null}
					</div>
				);
			}
		},
		{
			title: <div className="noselection cursor-pointer">{t("models.stakefactor.name_plural")}</div>,
			dataIndex: 'stakefactors',
			key: 'stakefactors',
			width: 130,
			className: 'text-center pr-1',
			sortable: (a: any, b: any) => {
				const scoreA = parseInt(a.stakefactors)
				const scoreB = parseInt(b.stakefactors)

				if (scoreA < scoreB) return -1;
				if (scoreA > scoreB) return 1;
				return 0
			},
			render: (text: any, record: any) => {
				return (
					<div className={["mr-1", maxStakefactors === record?.stakefactors ? "max" : "", minStakefactors === record?.stakefactors ? "min" : ""].join(" ")}>
						{record?.stakefactors !== null ? record?.stakefactors.toFixed(0) : null}
					</div>
				);
			}
		},
		{
			title: <div className="noselection cursor-pointer">{t("models.option.fields.score")}</div>,
			dataIndex: 'score',
			key: 'score',
			width: 190,
			className: 'text-center pr-1',
			defaultSortOrder: 'descend',
			sortable: (a: any, b: any) => {
				const scoreA = parseInt(a.score)
				const scoreB = parseInt(b.score)

				if (scoreA < scoreB) return -1;
				if (scoreA > scoreB) return 1;
				return 0
			},
			render: (text: any, record: any) => {
				return (
					<div className={["mr-1 Score", maxScore === record?.score ? "max" : "", minScore === record?.score ? "min" : ""].join(" ")}>
						{record.score.toFixed(0)}
					</div >
				);
			}
		},
		{
			title: t('models.option.fields.comments_short'),
			dataIndex: 'comment',
			key: 'comment',
			width: 100,
			className: 'text-center',
			render: (text: any, record: any) => {
				return (
					<Tooltip title={record.comments || t("models.option.messages.no_comments")}>
						<Button
							shape="circle"
							onClick={
								() => setEditedItem(record)
							}
							className={record.comments ? 'active comments' : 'comments'}
						>
							<FontAwesomeIcon icon={record.comments ? faCommentPlain : faComment} />
						</Button>
					</Tooltip >
				);
			}
		},
	];

	return (
		<div className="Results">
			<Tabs className="w-100" type="card" tabBarExtraContent={
				<Button
					type="primary"
					icon={<FontAwesomeIcon icon={faFileExcel} />}
					onClick={() => generateExcel(options, stakefactors, indicators, constraints, scoresList, t)}
					size="small"
				>
					{t("actions.export.excel")}
				</Button>
			}>
				<Tabs.TabPane tab={t("pages.results.tabs.ranking")} key="1">
					<Flex vertical={true} align='center' justify='center' className="Result mb-3" >
						<Flex justify="space-between" align="center" style={{ width: '100%', maxWidth: '1200px', marginBottom: '16px' }}>
							<div></div>
							<Button
								type="primary"
								icon={<FontAwesomeIcon icon={faFileImage} />}
								onClick={exportRankingAsImage}
								size="small"
							>
								{t("actions.export.image")}
							</Button>
						</Flex>
						<Table
							pagination={false}
							dataSource={scoresList}
							columns={columns}
							size="small"
							style={{ maxWidth: '1200px' }}
						/>
					</Flex>

				</Tabs.TabPane>

				<Tabs.TabPane tab={t("pages.results.tabs.details")} key="2">
					<ResultOption {...props} score={scoreDetails}></ResultOption>
				</Tabs.TabPane>

				<Tabs.TabPane tab={t("pages.results.tabs.summary")} key="3">
					<Flex justify="space-between" align="center" style={{ width: '100%', marginBottom: '16px' }}>
						<div></div>
						<Button
							type="primary"
							icon={<FontAwesomeIcon icon={faFileImage} />}
							onClick={exportVisualizationAsImage}
							size="small"
						>
							{t("actions.export.image")}
						</Button>
					</Flex>

					<Nav
						fullWidth
						count={props.options.length}
						options={props.options}
						index={props.optionIndex}
						onSelect={(index: number) => {
							props.setOptionIndex(index);
						}}
					>
						<Typography.Title level={2}>
							<small style={{ marginRight: '0.5rem' }}>{t("models.option.name")} {(props.optionIndex % props.options.length) + 1}:</small>
							{currentOption.name}{' '}

							<small>
								{currentOption.isGutDecision ? (
									<div className="GutDecision">
										<FontAwesomeIcon icon={faHeart} />
										Gut decision {props.experienceLevel == 1 ? ' - Not experienced' : null}
										{props.experienceLevel == 2 ? ' - Partially experienced' : null}
										{props.experienceLevel == 3 ? ' - Highly experienced' : null}
									</div>
								) : null}
							</small>

							{currentOption.comments && <Tooltip title={currentOption.comments || t("models.option.messages.no_comments")}>
								<Button
									shape="circle"
									className={currentOption.comments ? 'active comments ml-1' : 'comments ml-1'}
								>
									<FontAwesomeIcon icon={faCommentPlain} style={{ color: "#2EA0DF" }} />
								</Button>
							</Tooltip>
							}

						</Typography.Title>
					</Nav>

					<div className='SynthesisGraphWrapper text-center m-auto' style={{
						width: "700px"
					}}>
						<Tooltip title="Scenario score">
							{score !== null && <div className="score">{score?.toFixed(0)} <small>max 100</small></div>}
						</Tooltip>

						<RadarChart
							data={scores}
							width={700}
							height={600}
							axisConfig={compact([
								indicators.length > 0 && { name: 'indicators', label: t('models.indicator.name_plural'), max: 100 },
								constraints.length > 0 && { name: "constraints", label: t('models.constraint.name_plural'), max: 100 },
								stakefactors.length > 0 && { name: "stakefactors", label: t('models.stakefactor.name_plural'), max: 100 },
							])}

							onClick={(scenarioId) => {
								const index = scenarioId ? options.findIndex((s: any) => s.id === scenarioId) : null;
								props.setOptionIndex(index !== -1 ? index : null);
							}}
						/>
					</div>
				</Tabs.TabPane>
			</Tabs>

			{editedItem && (
				<OptionEditionModal
					onHide={() => {
						setEditedItem(null);
					}}
					onSave={(values: Object) => {
						handleUpdateOption(editedItem.id, values);
						setEditedItem(null);
					}}
					id={editedItem?.id}
					values={{
						name: editedItem?.name,
						description: editedItem?.description,
						comments: editedItem?.comments,
					}}
				></OptionEditionModal>
			)}
		</div>


	);
}

export default Results;
